import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import PageLayout from "../components/layout/PageLayout";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useUser } from "../Context/UserContext";
import { useCompany } from "../Context/CompanyContext";
import API_URL from "../config";

const Invitacion = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, fetchUserInfo } = useUser();
  const { fetchCompanies } = useCompany();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [tender, setTender] = useState(null);

  const [form, setForm] = useState({
    nombre_empresa: "",
    ruc: "",
    contacto_nombre: "",
    contacto_email: "",
    contacto_telefono: "",
  });
  const [buscandoRuc, setBuscandoRuc] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await fetch(`${API_URL}/licit/tender/invite/${token}`);
        const data = await response.json();
        if (!response.ok) {
          setError(data.error || "Invitación no encontrada");
          return;
        }
        setInvite(data.invite);
        setTender(data.tender);
        setForm((prev) => ({
          ...prev,
          contacto_nombre: data.invite.contacto_nombre || "",
          contacto_email: data.invite.contacto_email || "",
          contacto_telefono: data.invite.contacto_telefono || "",
        }));
      } catch (err) {
        console.error("Error al cargar la invitación:", err);
        setError("Error en la conexión con el servidor. Inténtalo más tarde.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBuscarRuc = async () => {
    if (!form.ruc) return;
    setBuscandoRuc(true);
    try {
      const response = await fetch(`${API_URL}/bs/sunat-lookup?ruc=${encodeURIComponent(form.ruc)}`);
      if (response.ok) {
        const data = await response.json();
        setForm((prev) => ({ ...prev, nombre_empresa: data.razon_social || prev.nombre_empresa }));
      } else {
        toast.info("No encontramos ese RUC en SUNAT, ingresa el nombre de la empresa manualmente.");
      }
    } catch (err) {
      console.error("Error al buscar RUC:", err);
    } finally {
      setBuscandoRuc(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/licit/tender/invite/${token}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Error en la solicitud. Inténtalo más tarde.");
        return;
      }

      localStorage.setItem(`access_token_${data.id_user}`, data.access_token);
      localStorage.setItem(`refresh_token_${data.id_user}`, data.refresh_token);
      localStorage.setItem("current_user_id", data.id_user);

      await fetchUserInfo();
      await fetchCompanies();

      toast.success("Cuenta creada. Ahora puedes postular a la licitación.");
      navigate(`/ver-licitacion/${data.id_tender}/procu`);
    } catch (err) {
      console.error("Error al registrar desde invitación:", err);
      setError("Error en la conexión con el servidor. Inténtalo más tarde.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Invitación a licitación - Procu</title>
      </Helmet>

      <div className="flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6">
            {error && !tender ? (
              <div className="text-center">
                <h2 className="text-xl font-semibold text-foreground">
                  No pudimos abrir esta invitación
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{error}</p>
                <Link to="/" className="mt-4 inline-block text-primary hover:underline">
                  Volver a Procu
                </Link>
              </div>
            ) : user || invite?.estado !== "enviado" ? (
              <div className="text-center">
                <h2 className="text-xl font-semibold text-foreground">
                  {tender?.titulo}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  {user
                    ? "Ya tienes una sesión activa. Continúa a la licitación para postular."
                    : "Esta invitación ya fue utilizada. Inicia sesión para continuar."}
                </p>
                <div className="mt-6 flex justify-center gap-2">
                  {user ? (
                    <Button onClick={() => navigate(`/ver-licitacion/${tender.id_tender}/procu`)}>
                      Ver licitación
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/login")}>Iniciar sesión</Button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-foreground">
                  Te invitaron a cotizar
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  <strong className="text-foreground">{tender?.titulo}</strong> — {tender?.categoria}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Regístrate con los datos básicos de tu empresa y cotiza de inmediato. Podrás
                  completar el resto de tu perfil más adelante.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
                  <div>
                    <label htmlFor="ruc" className="mb-1.5 block text-sm font-medium text-foreground">
                      RUC
                    </label>
                    <div className="flex gap-2">
                      <Input
                        id="ruc"
                        name="ruc"
                        value={form.ruc}
                        onChange={handleChange}
                        required
                      />
                      <Button type="button" variant="outline" onClick={handleBuscarRuc} disabled={buscandoRuc}>
                        {buscandoRuc ? "Buscando..." : "Buscar"}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="nombre_empresa" className="mb-1.5 block text-sm font-medium text-foreground">
                      Nombre de la empresa
                    </label>
                    <Input
                      id="nombre_empresa"
                      name="nombre_empresa"
                      value={form.nombre_empresa}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contacto_nombre" className="mb-1.5 block text-sm font-medium text-foreground">
                      Nombre de contacto
                    </label>
                    <Input
                      id="contacto_nombre"
                      name="contacto_nombre"
                      value={form.contacto_nombre}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contacto_email" className="mb-1.5 block text-sm font-medium text-foreground">
                      Correo de contacto
                    </label>
                    <Input
                      id="contacto_email"
                      name="contacto_email"
                      type="email"
                      value={form.contacto_email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contacto_telefono" className="mb-1.5 block text-sm font-medium text-foreground">
                      Teléfono (opcional)
                    </label>
                    <Input
                      id="contacto_telefono"
                      name="contacto_telefono"
                      value={form.contacto_telefono}
                      onChange={handleChange}
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Creando cuenta..." : "Crear cuenta y cotizar"}
                  </Button>
                  {error && <p className="text-center text-sm font-semibold text-destructive">{error}</p>}
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default Invitacion;
